// src/features/streams/simulcast/simulcastCache.ts

import type { StreamViewData } from '@/lib/types/Stream';
import type { ISimulcastAllData } from '@/lib/types/Simulcast';
import { SimulcastStatusType } from '@/lib/types/Simulcast';

/**
 * Pure cache patch helpers for simulcast data within StreamViewData.
 * All methods return new objects — no in-place mutation of query cache data.
 */
export class SimulcastCacheUtils {
  /** Replace entire simulcasts array (for RTMP create/update/delete responses). */
  static replaceSimulcasts(stream: StreamViewData, simulcasts: ISimulcastAllData[]): StreamViewData {
    return { ...stream, simulcasts };
  }

  /** Upsert a single simulcast (for FB/YT create/update responses). */
  static upsertSimulcast(stream: StreamViewData, simulcast: ISimulcastAllData): StreamViewData {
    const existing = stream.simulcasts ?? [];
    const idx = existing.findIndex(s => s.core.simulcastId === simulcast.core.simulcastId);
    const updated = idx >= 0
      ? existing.map((s, i) => (i === idx ? simulcast : s))
      : [...existing, simulcast];
    return { ...stream, simulcasts: updated };
  }

  /** Patch status field only (for start/stop — void response). Also clears error fields. */
  static patchSimulcastStatus(stream: StreamViewData, simulcastId: number, status: SimulcastStatusType): StreamViewData {
    const simulcasts = (stream.simulcasts ?? []).map(s =>
      s.core.simulcastId === simulcastId
        ? {
            ...s,
            core: {
              ...s.core,
              simulcastStatusType: status,
              lastErrorHttpStatusCode: null,
              lastErrorSafeMsg: null,
              lastErrorCreated: null,
            },
          }
        : s,
    );
    return { ...stream, simulcasts };
  }

  /** Clear errors by extPlatAuthId (for reconnect success). */
  static clearErrorsByExtPlatAuthId(stream: StreamViewData, extPlatAuthId: number): StreamViewData {
    const simulcasts = (stream.simulcasts ?? []).map(s =>
      s.core.extPlatAuthId === extPlatAuthId
        ? {
            ...s,
            core: {
              ...s.core,
              lastErrorHttpStatusCode: null,
              lastErrorSafeMsg: null,
              lastErrorCreated: null,
            },
          }
        : s,
    );
    return { ...stream, simulcasts };
  }
}
